import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiEdit2, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { useAppSelector } from '../../../redux/hooks';
import { selectSelectedLeague, selectSelectedLeagueId } from '../../../redux/slice/selectedLeagueSlice';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import { Images } from '../../../utils/imageConstants';
import { canManageWaiversInLeague } from '../../../utils/leagueAccess';
import BreadCrumb from '../../../components/BreadCrumb';
import { deleteWaiver, fetchLeagueWaivers, type IWaiver } from '../tournaments/api/waiverApi';
import './WaiverManagement.scss';

function formatDate(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getWaiverErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: unknown } })?.response;
  const data = response?.data;
  if (data && typeof data === 'object' && 'message' in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (typeof data === 'string' && data.trim()) return data;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

const WaiverManagement: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<IWaiver | null>(null);

  const canManage = canManageWaiversInLeague(selectedLeague);

  const { data: waivers, isLoading } = useQuery<IWaiver[]>({
    queryKey: [QUERY_KEY.leagueWaivers, selectedLeagueId, 'waivers'],
    queryFn: () => (selectedLeagueId ? fetchLeagueWaivers(selectedLeagueId) : Promise.resolve([])),
    enabled: !!selectedLeagueId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (waiver: IWaiver) => {
      if (!selectedLeagueId) throw new Error('League is missing.');
      await deleteWaiver(selectedLeagueId, waiver.waiverId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.leagueWaivers, selectedLeagueId] });
      toast.success('Waiver deleted successfully.');
      setDeleteTarget(null);
    },
    onError: (error) => {
      toast.error(getWaiverErrorMessage(error, 'Failed to delete waiver.'));
    },
  });

  const filteredWaivers = useMemo(() => {
    const list = waivers ?? [];
    const query = search.trim().toLowerCase();
    if (!query) return list;
    return list.filter(
      (w) => w.name.toLowerCase().includes(query) || (w.description ?? '').toLowerCase().includes(query)
    );
  }, [waivers, search]);

  return (
    <div className="main-middle-content-wrap">
      <div className="waivers-wrapper custom-container">
        <BreadCrumb />
        <div className="waivers-header">
          <h1 className="heading">Waiver Management</h1>
          <div className="waivers-header-right">
            <div className="waivers-search">
              <FiSearch size={16} />
              <input
                type="text"
                placeholder="Search Waiver..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canManage && (
              <button
                type="button"
                className="waivers-btn waivers-btn--primary"
                onClick={() => navigate('/create-waiver')}
              >
                <FiPlus size={16} />
                <span>Create New Waiver Form</span>
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="waivers-loading">Loading...</div>
        ) : filteredWaivers.length === 0 ? (
          <div className="no-data-found">
            <img src={Images.my_leagues_no} alt="No Waivers Found" />
            <p className="no-data-found-text">
              {waivers && waivers.length > 0 ? 'No waivers match your search' : 'No Waivers Found'}
            </p>
          </div>
        ) : (
          <div className="waivers-list-wrap">
            <table className="waivers-list-table">
              <thead>
                <tr>
                  <th>Waiver Name</th>
                  <th>Description</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWaivers.map((waiver) => (
                  <tr key={waiver.waiverId}>
                    <td>
                      <span className="waivers-list-name">{waiver.name}</span>
                    </td>
                    <td>
                      <span className="waivers-list-value">{waiver.description || '-'}</span>
                    </td>
                    <td>
                      <span className="waivers-list-value">{formatDate(waiver.createdAt)}</span>
                    </td>
                    <td>
                      {canManage ? (
                        <div className="waivers-list-actions">
                          <button
                            type="button"
                            className="waivers-list-action-btn waivers-list-action-btn--danger"
                            onClick={() => setDeleteTarget(waiver)}
                            aria-label={`Delete ${waiver.name}`}
                          >
                            <FiTrash2 size={15} />
                          </button>
                          <button
                            type="button"
                            className="waivers-list-action-btn"
                            onClick={() => navigate(`/create-waiver/${waiver.waiverId}`)}
                            aria-label={`Edit ${waiver.name}`}
                          >
                            <FiEdit2 size={15} />
                          </button>
                        </div>
                      ) : (
                        <span className="waivers-list-value">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {deleteTarget &&
          createPortal(
            <div
              className="waiver-delete-modal-overlay"
              onClick={() => !deleteMutation.isPending && setDeleteTarget(null)}
              aria-modal="true"
              role="dialog"
            >
              <div className="waiver-delete-modal" onClick={(e) => e.stopPropagation()}>
                <div className="waiver-delete-modal__icon">
                  <FiTrash2 size={34} />
                </div>
                <h2 className="waiver-delete-modal__title">Delete Waiver?</h2>
                <p className="waiver-delete-modal__text">
                  "{deleteTarget.name}" will be permanently deleted. This cannot be undone.
                </p>
                <div className="waiver-delete-modal__actions">
                  <button
                    type="button"
                    className="waiver-delete-modal__btn waiver-delete-modal__btn--cancel"
                    onClick={() => setDeleteTarget(null)}
                    disabled={deleteMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="waiver-delete-modal__btn waiver-delete-modal__btn--delete"
                    onClick={() => deleteMutation.mutate(deleteTarget)}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default WaiverManagement;
